/**
 * Tailwind CSS implementation of the ContentBlock component.
 * Renders the whole component (card, content area, loader, footer) with plain
 * elements and the Seeds content-block CSS classes from content-block.css.
 * Child components (ContentHeader, Collapsible, Loader) are composed unchanged.
 */

import React, { useState } from "react";
import { Loader } from "@sproutsocial/seeds-react-loader";
import { Collapsible } from "@sproutsocial/seeds-react-collapsible";
import {
  ContentHeader,
  CollapsibleContentHeader,
} from "@sproutsocial/seeds-react-content-header";
import type {
  ContentBlockBaseProps,
  ContentBlockProps,
  CollapsibleContentBlockProps,
} from "./ContentBlock";

// Utility for merging class names properly.
// cn is NOT exported from @sproutsocial/seeds-react-utilities, so it is
// reproduced locally here (mirrors ButtonTailwind.tsx).
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export type ContentFooterProps = {
  footerContent?: React.ReactNode;
  footerActions?: React.ReactNode;
  className?: string;
};

export const ContentFooterTailwind = ({
  footerContent,
  footerActions,
  className,
}: ContentFooterProps) => (
  <div className={cn("seeds-content-block-footer", className)}>
    {footerContent && <span>{footerContent}</span>}
    {footerActions && <span>{footerActions}</span>}
  </div>
);

const ContentArea = ({
  isLoading,
  children,
  flushLayout,
  hasFooter,
  contentProps,
}: Pick<
  ContentBlockBaseProps,
  "isLoading" | "children" | "flushLayout" | "contentProps"
> & {
  hasFooter?: boolean;
}) => {
  // By construction, any contentProps reaching the Tailwind path carries no
  // styled-system props (those route to ContentBlockStyled via hasStyledProps),
  // so the rest is pure DOM attributes safe to spread onto a div. Pull className
  // out and merge it via cn so the seeds-content-block-content class is
  // preserved, rendering className after the spread so the merged value wins
  // (mirrors ButtonTailwind).
  const { className, ...domProps } = contentProps ?? {};
  return (
    <div
      {...(domProps as React.HTMLAttributes<HTMLDivElement>)}
      className={cn(
        "seeds-content-block-content",
        {
          flush: !!flushLayout,
          "no-footer": !hasFooter,
        },
        className
      )}
    >
      {isLoading ? (
        <div className="seeds-content-block-loader">
          <Loader delay={false} />
        </div>
      ) : (
        children
      )}
    </div>
  );
};

const CollapsibleContentBlock = ({
  title,
  subtitle,
  titleAs = "h2",
  subtitleAs = "h3",
  children,
  isLoading,
  headerActions,
  rightTitleElement,
  flushLayout,
  footerContent,
  footerActions,
  contentProps,
  isOpen: controlledIsOpen,
  defaultOpen = true,
  onOpenChange,
}: Omit<CollapsibleContentBlockProps, "isCollapsible">) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen;
  const hasFooter = !!(footerContent || footerActions);

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Collapsible isOpen={open} onOpenChange={handleOpenChange}>
      <section className="seeds-content-block">
        <CollapsibleContentHeader
          title={title}
          subtitle={subtitle}
          headingLevel={titleAs}
          subtitleAs={subtitleAs}
          headerActions={headerActions}
          rightTitleElement={rightTitleElement}
          trigger={Collapsible.Trigger}
          triggerPosition="left"
        />
        <Collapsible.Panel>
          <ContentArea
            isLoading={isLoading}
            flushLayout={flushLayout}
            hasFooter={hasFooter}
            contentProps={contentProps}
          >
            {children}
          </ContentArea>
          {hasFooter && (
            <ContentFooterTailwind
              footerContent={footerContent}
              footerActions={footerActions}
            />
          )}
        </Collapsible.Panel>
      </section>
    </Collapsible>
  );
};

/**
 * Tailwind/CSS-class implementation of the full ContentBlock. Preferred path;
 * the Hybrid routes here unless `contentProps` carries styled-system props.
 */
export const ContentBlockTailwind = ({
  isCollapsible,
  ...props
}: ContentBlockProps) => {
  if (isCollapsible) {
    return <CollapsibleContentBlock {...props} />;
  }

  const {
    title,
    subtitle,
    titleAs = "h2",
    subtitleAs = "h3",
    children,
    isLoading,
    headerActions,
    rightTitleElement,
    flushLayout,
    footerContent,
    footerActions,
    contentProps,
  } = props;

  const hasFooter = !!(footerContent || footerActions);

  return (
    <section className="seeds-content-block">
      <ContentHeader
        title={title}
        subtitle={subtitle}
        headingLevel={titleAs}
        subtitleAs={subtitleAs}
        headerActions={headerActions}
        rightTitleElement={rightTitleElement}
      />
      <ContentArea
        isLoading={isLoading}
        flushLayout={flushLayout}
        hasFooter={hasFooter}
        contentProps={contentProps}
      >
        {children}
      </ContentArea>
      {hasFooter && (
        <ContentFooterTailwind
          footerContent={footerContent}
          footerActions={footerActions}
        />
      )}
    </section>
  );
};
